import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVerifyEmailMutation } from '@/redux/internal-services/onboardingApi';
import LeftSidePanel from '@/components/UI/LeftSidePanel';

const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifyEmail] = useVerifyEmailMutation();
  const [status, setStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('failed');
      return;
    }
    verifyEmail({ token })
      .unwrap()
      .then(() => {
        setStatus('success');
        setTimeout(() => navigate('/login'), 2500);
      })
      .catch(() => setStatus('failed'));
  }, []);

  return (
    <div className="force-light flex min-h-screen bg-ui-raised">
      <LeftSidePanel />

      <div className="flex-1 flex items-center justify-center bg-ui-raised px-6 py-12">
        <div className="bg-ui-card rounded-2xl shadow-md px-10 py-10 text-center w-full max-w-md">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
            status === 'success' ? 'bg-green-100' : status === 'failed' ? 'bg-red-100' : 'bg-brand-light'
          }`}>
            {status === 'success' ? (
              <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-green-600" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : status === 'failed' ? (
              <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-red-500" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-brand-dark" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            )}
          </div>

          <h2 className="text-2xl font-bold text-ui-text mb-2">
            {status === 'success'
              ? t('onboarding.verifyEmail.successTitle', 'Email Verified!')
              : status === 'failed'
              ? t('onboarding.verifyEmail.failedTitle', 'Verification Failed')
              : t('onboarding.verifyEmail.title', 'Verify Your Account')}
          </h2>

          {status === 'verifying' && (
            <p className="text-ui-text2 text-sm">{t('onboarding.verifyEmail.verifying', 'Verifying your email address…')}</p>
          )}

          {status === 'success' && (
            <p className="text-ui-text2 text-sm">{t('onboarding.verifyEmail.success', 'Your email has been verified. Redirecting to login…')}</p>
          )}

          {status === 'failed' && (
            <>
              <p className="text-ui-text2 text-sm mb-6">{t('onboarding.verifyEmail.failed', 'This link is invalid or has expired.')}</p>
              <button
                onClick={() => navigate('/register')}
                className="w-full bg-[#1c1849] text-white py-3 rounded-lg font-semibold text-sm hover:bg-[#2d2b6b] transition"
              >
                Back to Registration
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
